import { useState } from 'react'

import {
  BarraProgresso,
  Botao,
  Campo,
  Cartao,
  classesEntrada,
  Escala,
} from '@/componentes/ui'
import { fusoDoDispositivo } from '@/lib/datas'
import { estaInstalado, podePedirPermissao } from '@/lib/instalacao'
import { ativarNotificacoes } from '@/lib/push'
import { supabase } from '@/lib/supabase'
import { comprimirImagem } from '@/lib/imagens'
import { mensagemDeErro } from '@/lib/utilitarios'
import { FATOR_AGUA_ML_POR_KG, VERSAO_CONSENTIMENTO_SAUDE, VERSAO_TERMOS } from '@/tipos/dominio'
import type { RodaVidaArea } from '@/tipos/tabelas'
import {
  CONSENTIMENTO_SAUDE,
  CONSENTIMENTO_SAUDE_GARANTIAS,
  CONSENTIMENTO_SAUDE_TITULO,
  DADOS_SENSIVEIS_COLETADOS,
  DISCLAIMER,
  TERMOS_RESUMO,
} from './textosLegais'

/**
 * Onboarding obrigatório (seção 3 da especificação), reordenado.
 *
 * A ordem do documento começa pelos termos. Aqui a permissão de
 * notificação vem antes, logo depois da instalação, porque no iOS ela
 * só pode ser pedida a partir do app já instalado — e é o único momento
 * em que a usuária está disponível para conceder. Perder essa janela
 * significa 28 dias sem lembrete.
 */

const ETAPAS = [
  'nome',
  'notificacoes',
  'termos',
  'saude',
  'foto',
  'medidas',
  'agua',
  'roda',
] as const
type Etapa = (typeof ETAPAS)[number]

interface DadosOnboarding {
  pesoKg: number | null
  alturaCm: number | null
  fotoAntes: File | null
  notas: Record<string, number>
}

export function PaginaOnboarding({
  matriculaId,
  userId,
  areasRodaVida,
  aoConcluir,
}: {
  matriculaId: string
  userId: string
  areasRodaVida: RodaVidaArea[]
  aoConcluir: () => void
}) {
  const [indice, setIndice] = useState(0)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [dados, setDados] = useState<DadosOnboarding>({
    pesoKg: null,
    alturaCm: null,
    fotoAntes: null,
    notas: {},
  })

  const etapa = ETAPAS[indice] as Etapa
  const percentual = Math.round(((indice + 1) / ETAPAS.length) * 100)

  function avancar() {
    setErro(null)
    setIndice((atual) => Math.min(ETAPAS.length - 1, atual + 1))
  }

  function voltar() {
    setErro(null)
    setIndice((atual) => Math.max(0, atual - 1))
  }

  async function finalizar() {
    setSalvando(true)
    setErro(null)
    try {
      await gravarOnboarding({ matriculaId, userId, dados, areasRodaVida })
      aoConcluir()
    } catch (e) {
      setErro(mensagemDeErro(e, 'Não consegui salvar seus dados. Tente de novo em instantes.'))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col gap-5 px-5 py-8">
      <div>
        <BarraProgresso valor={percentual} rotulo="Progresso do cadastro" />
        <p className="mt-2 text-xs text-[var(--tinta-apagada)]">
          Passo {indice + 1} de {ETAPAS.length}
        </p>
      </div>

      <div className="flex-1">
        {etapa === 'nome' && <EtapaNome userId={userId} aoAvancar={avancar} />}
        {etapa === 'notificacoes' && <EtapaNotificacoes aoAvancar={avancar} />}
        {etapa === 'termos' && <EtapaTermos aoAvancar={avancar} />}
        {etapa === 'saude' && <EtapaConsentimentoSaude aoAvancar={avancar} />}
        {etapa === 'foto' && (
          <EtapaFoto
            arquivo={dados.fotoAntes}
            aoEscolher={(fotoAntes) => setDados((d) => ({ ...d, fotoAntes }))}
            aoAvancar={avancar}
          />
        )}
        {etapa === 'medidas' && (
          <EtapaMedidas
            peso={dados.pesoKg}
            altura={dados.alturaCm}
            aoMudar={(pesoKg, alturaCm) => setDados((d) => ({ ...d, pesoKg, alturaCm }))}
            aoAvancar={avancar}
          />
        )}
        {etapa === 'agua' && <EtapaAgua pesoKg={dados.pesoKg ?? 0} aoAvancar={avancar} />}
        {etapa === 'roda' && (
          <EtapaRodaVida
            areas={areasRodaVida}
            notas={dados.notas}
            aoMudar={(notas) => setDados((d) => ({ ...d, notas }))}
            aoConcluir={finalizar}
            salvando={salvando}
          />
        )}
      </div>

      {erro && (
        <p role="alert" className="rounded-2xl bg-pink/10 p-3 text-sm text-pink">
          {erro}
        </p>
      )}

      {indice > 0 && (
        <Botao variante="fantasma" tamanho="pequeno" onClick={voltar}>
          Voltar
        </Botao>
      )}
    </main>
  )
}

// ---------------------------------------------------------------------
// Etapa 0 — Nome
//
// O cadastro é só e-mail e código, sem nenhuma etapa que peça o nome.
// Sem isto, a saudação da tela inicial ("Bom dia, ...") ficava muda e a
// administração via "Sem nome" para toda participante, sempre. Salva na
// hora, não junto do resto no fim do onboarding: se ela abandonar o
// cadastro no meio, o nome já digitado não se perde.
// ---------------------------------------------------------------------

function EtapaNome({ userId, aoAvancar }: { userId: string; aoAvancar: () => void }) {
  const [nome, setNome] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  async function continuar(evento: React.FormEvent) {
    evento.preventDefault()
    const valor = nome.trim()
    if (!valor) return

    setSalvando(true)
    setErro(null)
    const { error } = await supabase.from('perfis').update({ nome: valor }).eq('id', userId)
    setSalvando(false)

    if (error) {
      setErro('Não consegui salvar. Tente de novo.')
      return
    }
    aoAvancar()
  }

  return (
    <form onSubmit={continuar} className="flex flex-col gap-4">
      <Cabecalho
        emoji="👋"
        titulo="Como podemos te chamar?"
        descricao="É assim que vamos te cumprimentar todo dia no app."
      />

      <Cartao>
        <input
          type="text"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Seu primeiro nome"
          autoFocus
          autoComplete="given-name"
          maxLength={60}
          className={classesEntrada}
        />
      </Cartao>

      {erro && (
        <p role="alert" className="text-sm text-pink">
          {erro}
        </p>
      )}

      <Botao type="submit" tamanho="bloco" disabled={!nome.trim()} carregando={salvando}>
        Continuar
      </Botao>
    </form>
  )
}

// ---------------------------------------------------------------------
// Etapa 1 — Notificações
// ---------------------------------------------------------------------

function EtapaNotificacoes({ aoAvancar }: { aoAvancar: () => void }) {
  const [estado, setEstado] = useState<'inicial' | 'negada' | 'sem_suporte'>('inicial')
  const [processando, setProcessando] = useState(false)

  async function ativar() {
    setProcessando(true)
    const resultado = await ativarNotificacoes()
    setProcessando(false)

    if (resultado.estado === 'ok') return aoAvancar()
    if (resultado.estado === 'negada') return setEstado('negada')
    setEstado('sem_suporte')
  }

  return (
    <div className="flex flex-col gap-4">
      <Cabecalho
        emoji="🔔"
        titulo="Ative seus lembretes"
        descricao="São eles que te lembram da água em jejum, do chá pós-almoço, da caminhada, do sol e do diário. Sem eles, o programa vira um PDF de novo."
      />

      <Cartao className="flex flex-col gap-2 text-sm text-[var(--tinta-suave)]">
        {[
          'Bom dia! Sua missão do dia está disponível.',
          'Hora da água em jejum.',
          'Não esqueça o chá pós-almoço.',
          'Caminhada do dia: pelo menos 20 minutos.',
          'Não esqueça de preencher seu diário de hoje.',
        ].map((texto) => (
          <p key={texto} className="flex items-start gap-2">
            <span aria-hidden>·</span>
            {texto}
          </p>
        ))}
        <p className="mt-1 text-xs text-[var(--tinta-apagada)]">
          Você pode ajustar cada horário depois, no seu perfil.
        </p>
      </Cartao>

      {estado === 'negada' && (
        <Cartao className="bg-pink/10 text-sm leading-relaxed">
          Você recusou as notificações. Para ativar depois, vá em Ajustes do celular &rsaquo;
          Notificações &rsaquo; Viver. Dá para seguir sem elas, mas os lembretes não vão chegar.
        </Cartao>
      )}

      {estado === 'sem_suporte' && (
        <Cartao className="bg-dourado/15 text-sm leading-relaxed">
          {podePedirPermissao() || estaInstalado()
            ? 'Este navegador não suporta notificações. Você pode seguir, mas sem lembretes.'
            : 'Abra o app pelo ícone na tela de início para ativar os lembretes.'}
        </Cartao>
      )}

      <Botao tamanho="bloco" onClick={ativar} carregando={processando}>
        Ativar meus lembretes
      </Botao>

      {estado !== 'inicial' && (
        <Botao variante="fantasma" tamanho="pequeno" onClick={aoAvancar}>
          Seguir sem lembretes
        </Botao>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------
// Etapa 2 — Termos e disclaimer (seção 16)
// ---------------------------------------------------------------------

function EtapaTermos({ aoAvancar }: { aoAvancar: () => void }) {
  const [aceito, setAceito] = useState(false)

  return (
    <div className="flex flex-col gap-4">
      <Cabecalho emoji="📄" titulo="Antes de começar" />

      <Cartao className="border-2 border-dourado bg-dourado/10">
        <h3 className="mb-2 font-titulo text-sm font-semibold">Aviso importante</h3>
        <p className="text-sm leading-relaxed text-[var(--tinta-suave)]">{DISCLAIMER}</p>
      </Cartao>

      <Cartao className="flex flex-col gap-2">
        {TERMOS_RESUMO.map((item) => (
          <p key={item} className="flex items-start gap-2 text-sm leading-snug">
            <span className="text-verde" aria-hidden>
              ✓
            </span>
            {item}
          </p>
        ))}
      </Cartao>

      <CaixaAceite
        marcado={aceito}
        aoAlternar={() => setAceito((v) => !v)}
        texto="Li e concordo com os termos de uso e com o aviso acima."
      />

      <Botao tamanho="bloco" disabled={!aceito} onClick={aoAvancar}>
        Continuar
      </Botao>
    </div>
  )
}

// ---------------------------------------------------------------------
// Etapa 3 — Consentimento de dados de saúde (LGPD)
// ---------------------------------------------------------------------

function EtapaConsentimentoSaude({ aoAvancar }: { aoAvancar: () => void }) {
  const [aceito, setAceito] = useState(false)

  return (
    <div className="flex flex-col gap-4">
      <Cabecalho emoji="🔒" titulo={CONSENTIMENTO_SAUDE_TITULO} />

      <Cartao>
        <p className="text-sm leading-relaxed text-[var(--tinta-suave)]">{CONSENTIMENTO_SAUDE}</p>

        <ul className="mt-3 flex flex-col gap-2">
          {DADOS_SENSIVEIS_COLETADOS.map((dado) => (
            <li key={dado.rotulo} className="text-sm leading-snug">
              <strong className="font-titulo font-semibold">{dado.rotulo}</strong>
              <span className="text-[var(--tinta-apagada)]"> — {dado.detalhe}</span>
            </li>
          ))}
        </ul>
      </Cartao>

      <Cartao className="bg-verde/10">
        <h3 className="mb-2 font-titulo text-sm font-semibold">Seus direitos</h3>
        <ul className="flex flex-col gap-1.5">
          {CONSENTIMENTO_SAUDE_GARANTIAS.map((garantia) => (
            <li key={garantia} className="flex items-start gap-2 text-sm leading-snug">
              <span className="text-verde" aria-hidden>
                ✓
              </span>
              {garantia}
            </li>
          ))}
        </ul>
      </Cartao>

      <CaixaAceite
        marcado={aceito}
        aoAlternar={() => setAceito((v) => !v)}
        texto="Autorizo o Projeto Viver a tratar meus dados de saúde para o meu acompanhamento no programa."
      />

      <Botao tamanho="bloco" disabled={!aceito} onClick={aoAvancar}>
        Autorizar e continuar
      </Botao>
    </div>
  )
}

// ---------------------------------------------------------------------
// Etapa 4 — Foto do "antes"
// ---------------------------------------------------------------------

function EtapaFoto({
  arquivo,
  aoEscolher,
  aoAvancar,
}: {
  arquivo: File | null
  aoEscolher: (arquivo: File | null) => void
  aoAvancar: () => void
}) {
  const [previa, setPrevia] = useState<string | null>(null)

  function selecionar(evento: React.ChangeEvent<HTMLInputElement>) {
    const escolhido = evento.target.files?.[0] ?? null
    aoEscolher(escolhido)
    setPrevia(escolhido ? URL.createObjectURL(escolhido) : null)
  }

  return (
    <div className="flex flex-col gap-4">
      <Cabecalho
        emoji="📸"
        titulo="Sua foto de hoje"
        descricao="No fim dos 28 dias você tira outra e compara. É a prova mais honesta da sua evolução."
      />

      <Cartao className="bg-verde/10 text-sm leading-relaxed text-[var(--tinta-suave)]">
        Sua foto fica em área privada, visível só para você. Nem a administração do programa tem
        acesso.
      </Cartao>

      <label className="block cursor-pointer">
        {/* Sem `capture`: no iOS/Android ele força a câmera direto e
            esconde a opção de escolher da galeria. Sem o atributo, o
            sistema mostra as duas opções. */}
        <input type="file" accept="image/*" className="sr-only" onChange={selecionar} />
        <div className="flex aspect-3/4 w-full items-center justify-center overflow-hidden rounded-[var(--radius-cartao)] border-2 border-dashed border-coral/50 bg-[var(--cartao)]">
          {previa ? (
            <img src={previa} alt="Prévia da sua foto" className="size-full object-cover" />
          ) : (
            <span className="flex flex-col items-center gap-2 text-[var(--tinta-apagada)]">
              <span className="text-4xl" aria-hidden>
                📷
              </span>
              <span className="font-titulo text-sm font-semibold">Tocar para tirar ou escolher</span>
            </span>
          )}
        </div>
      </label>

      <Botao tamanho="bloco" disabled={!arquivo} onClick={aoAvancar}>
        Continuar
      </Botao>
    </div>
  )
}

// ---------------------------------------------------------------------
// Etapa 5 — Peso e altura
// ---------------------------------------------------------------------

function EtapaMedidas({
  peso,
  altura,
  aoMudar,
  aoAvancar,
}: {
  peso: number | null
  altura: number | null
  aoMudar: (peso: number | null, altura: number | null) => void
  aoAvancar: () => void
}) {
  const imc = peso && altura ? peso / (altura / 100) ** 2 : null
  const valido = Boolean(peso && altura && peso > 0 && altura > 0)

  return (
    <div className="flex flex-col gap-4">
      <Cabecalho emoji="⚖️" titulo="Seu ponto de partida" />

      <Cartao className="flex flex-col gap-4">
        <Campo rotulo="Peso (kg)">
          <input
            type="number"
            inputMode="decimal"
            step="0.1"
            min="20"
            max="400"
            value={peso ?? ''}
            onChange={(e) => aoMudar(e.target.valueAsNumber || null, altura)}
            placeholder="68,5"
            className={classesEntrada}
          />
        </Campo>

        <Campo rotulo="Altura (cm)">
          <input
            type="number"
            inputMode="numeric"
            step="1"
            min="100"
            max="250"
            value={altura ?? ''}
            onChange={(e) => aoMudar(peso, e.target.valueAsNumber || null)}
            placeholder="165"
            className={classesEntrada}
          />
        </Campo>
      </Cartao>

      {imc && (
        <Cartao className="bg-dourado/15 text-center">
          <p className="font-titulo text-sm font-semibold text-[var(--tinta-suave)]">Seu IMC</p>
          <p className="font-titulo text-3xl font-bold">{imc.toFixed(1)}</p>
          <p className="mt-1 text-xs text-[var(--tinta-apagada)]">
            É só um número de referência. O que você vai sentir nos 28 dias importa mais.
          </p>
        </Cartao>
      )}

      <Botao tamanho="bloco" disabled={!valido} onClick={aoAvancar}>
        Continuar
      </Botao>
    </div>
  )
}

// ---------------------------------------------------------------------
// Etapa 6 — Água diária
// ---------------------------------------------------------------------

function EtapaAgua({ pesoKg, aoAvancar }: { pesoKg: number; aoAvancar: () => void }) {
  const ml = Math.round(pesoKg * FATOR_AGUA_ML_POR_KG)
  const litros = (ml / 1000).toFixed(1).replace('.', ',')
  const copos = Math.round(ml / 250)

  return (
    <div className="flex flex-col gap-4">
      <Cabecalho emoji="💧" titulo="Sua meta de água" />

      <Cartao className="bg-linear-to-br from-coral/15 to-dourado/15 text-center">
        <p className="font-titulo text-5xl font-bold text-coral">{litros}L</p>
        <p className="mt-1 font-titulo text-sm font-semibold text-[var(--tinta-suave)]">
          por dia · cerca de {copos} copos
        </p>
        <p className="mt-3 text-xs text-[var(--tinta-apagada)]">
          {pesoKg.toFixed(0)} kg × {FATOR_AGUA_ML_POR_KG} ml
        </p>
      </Cartao>

      <Cartao className="text-sm leading-relaxed text-[var(--tinta-suave)]">
        Comece pela água em jejum, logo ao acordar. É a primeira prática do dia e a que mais muda
        como você se sente pela manhã.
      </Cartao>

      <Botao tamanho="bloco" onClick={aoAvancar}>
        Continuar
      </Botao>
    </div>
  )
}

// ---------------------------------------------------------------------
// Etapa 7 — Roda da Vida (seção 3.1)
// ---------------------------------------------------------------------

function EtapaRodaVida({
  areas,
  notas,
  aoMudar,
  aoConcluir,
  salvando,
}: {
  areas: RodaVidaArea[]
  notas: Record<string, number>
  aoMudar: (notas: Record<string, number>) => void
  aoConcluir: () => void
  salvando: boolean
}) {
  const completo = areas.every((area) => notas[area.id] !== undefined)

  return (
    <div className="flex flex-col gap-4">
      <Cabecalho
        emoji="🎯"
        titulo="Roda da Vida"
        descricao="Dê uma nota de 0 a 10 para cada área. No fim dos 28 dias você responde de novo e vê a diferença."
      />

      <div className="flex flex-col gap-4">
        {areas.map((area) => (
          <Cartao key={area.id}>
            <Escala
              rotulo={area.nome}
              valor={notas[area.id] ?? null}
              aoMudar={(nota) => aoMudar({ ...notas, [area.id]: nota })}
              descricaoMin="Nada bem"
              descricaoMax="Ótima"
            />
          </Cartao>
        ))}
      </div>

      <Botao tamanho="bloco" disabled={!completo} carregando={salvando} onClick={aoConcluir}>
        Concluir e começar
      </Botao>
    </div>
  )
}

// ---------------------------------------------------------------------
// Auxiliares de apresentação
// ---------------------------------------------------------------------

function Cabecalho({
  emoji,
  titulo,
  descricao,
}: {
  emoji: string
  titulo: string
  descricao?: string
}) {
  return (
    <header>
      <span className="text-4xl" aria-hidden>
        {emoji}
      </span>
      <h1 className="mt-2 font-titulo text-xl font-bold">{titulo}</h1>
      {descricao && (
        <p className="mt-1.5 text-[15px] leading-relaxed text-[var(--tinta-suave)]">{descricao}</p>
      )}
    </header>
  )
}

function CaixaAceite({
  marcado,
  aoAlternar,
  texto,
}: {
  marcado: boolean
  aoAlternar: () => void
  texto: string
}) {
  return (
    <button
      type="button"
      onClick={aoAlternar}
      aria-pressed={marcado}
      className="flex items-start gap-3 rounded-2xl border-2 border-[var(--borda)] bg-[var(--cartao)] p-4 text-left"
    >
      <span
        className={`mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md border-2 ${
          marcado ? 'border-verde bg-verde text-white' : 'border-[var(--borda)]'
        }`}
        aria-hidden
      >
        {marcado && '✓'}
      </span>
      <span className="text-sm leading-snug">{texto}</span>
    </button>
  )
}

// ---------------------------------------------------------------------
// Gravação
// ---------------------------------------------------------------------

async function gravarOnboarding({
  matriculaId,
  userId,
  dados,
  areasRodaVida,
}: {
  matriculaId: string
  userId: string
  dados: DadosOnboarding
  areasRodaVida: RodaVidaArea[]
}) {
  // Consentimentos primeiro: sem base legal registrada, nenhum dado de
  // saúde pode ser gravado.
  const { error: erroConsentimento } = await supabase.from('consentimentos').upsert(
    [
      {
        user_id: userId,
        tipo: 'termos_uso',
        versao: VERSAO_TERMOS,
        user_agent: navigator.userAgent,
      },
      {
        user_id: userId,
        tipo: 'dados_saude',
        versao: VERSAO_CONSENTIMENTO_SAUDE,
        user_agent: navigator.userAgent,
      },
    ],
    { onConflict: 'user_id,tipo,versao', ignoreDuplicates: true },
  )
  if (erroConsentimento) throw erroConsentimento

  // Fuso do dispositivo — base do agendamento de push no servidor.
  await supabase.from('perfis').update({ fuso_horario: fusoDoDispositivo() }).eq('id', userId)

  if (dados.pesoKg && dados.alturaCm) {
    const { error } = await supabase.from('medidas').insert({
      matricula_id: matriculaId,
      momento: 'inicial',
      peso_kg: dados.pesoKg,
      altura_cm: dados.alturaCm,
    })
    if (error) throw error
  }

  if (dados.fotoAntes) {
    const comprimida = await comprimirImagem(dados.fotoAntes)
    const caminho = `${userId}/antes.webp`

    const { error: erroUpload } = await supabase.storage
      .from('fotos')
      .upload(caminho, comprimida, { upsert: true, contentType: 'image/webp' })
    if (erroUpload) throw erroUpload

    // onConflict explícito é obrigatório: sem ele, o upsert usa a chave
    // primária (id, sempre novo aqui) como alvo do conflito, nunca a
    // constraint unique(matricula_id, tipo) — numa segunda tentativa de
    // onboarding (após qualquer falha num passo posterior), a inserção
    // batia nessa constraint separada e travava a participante nesse
    // passo para sempre, sem saída.
    const { error: erroFoto } = await supabase
      .from('fotos')
      .upsert(
        { matricula_id: matriculaId, tipo: 'antes', storage_path: caminho },
        { onConflict: 'matricula_id,tipo' },
      )
    if (erroFoto) throw erroFoto
  }

  const respostas = areasRodaVida
    .filter((area) => dados.notas[area.id] !== undefined)
    .map((area) => ({
      matricula_id: matriculaId,
      area_id: area.id,
      momento: 'inicial' as const,
      nota: dados.notas[area.id] as number,
    }))

  if (respostas.length > 0) {
    const { error } = await supabase
      .from('roda_vida_respostas')
      .upsert(respostas, { onConflict: 'matricula_id,area_id,momento' })
    if (error) throw error
  }

  // Cria os lembretes a partir do modelo do programa.
  await supabase.rpc('criar_lembretes_da_matricula', { p_matricula_id: matriculaId })

  const { error: erroMatricula } = await supabase
    .from('matriculas')
    .update({ onboarding_completo: true })
    .eq('id', matriculaId)
  if (erroMatricula) throw erroMatricula
}
